import React from "react";
import { Link } from "react-router-dom";
import markUrl from "./goollooper-mark.svg";

/**
 * IXEE marketing website — ixee.ai (apex + www).
 * Copy: drafted by Dotty 2026-10-21; live copy approved by Uche 2026-10-30.
 * Header lockup added 2026-11-04 at Uche's direction: the Goollooper loop mark
 * beside the IXEE wordmark ("Ixee, by Goollooper" — his Goollooper–Ixee lockup,
 * adapted to this site's light header; mark vector in ./goollooper-mark.svg).
 * Design language matches the app: ink #161A1D, Space Grotesk display, Inter body.
 */

const APP_URL = "https://app.ixee.ai";

const Wordmark = ({ className = "text-3xl", markClassName = "h-9 w-9" }) => (
  <span className="inline-flex items-center gap-2.5">
    <img src={markUrl} alt="" aria-hidden="true" className={markClassName} />
    <span
      className={`font-bold tracking-wide text-[#161A1D] ${className}`}
      style={{ fontFamily: "'Space Grotesk', sans-serif" }}
    >
      IXEE
    </span>
  </span>
);

export const IxeeNav = () => (
  <header className="sticky top-0 z-40 bg-white/90 backdrop-blur border-b border-neutral-200">
    <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
      <Link to="/" aria-label="IXEE home">
        <Wordmark />
      </Link>
      <nav className="hidden md:flex items-center gap-8 text-sm text-neutral-600">
        <a href="#how" className="hover:text-[#161A1D]">How it works</a>
        <a href="#providers" className="hover:text-[#161A1D]">For providers</a>
        <a href="#aded" className="hover:text-[#161A1D]">AdEd</a>
        <a href="#dataroom" className="hover:text-[#161A1D]">Data Room</a>
      </nav>
      <a
        href={APP_URL}
        className="bg-[#161A1D] text-white text-sm font-medium px-4 py-2 rounded-full hover:bg-black"
      >
        Open the app
      </a>
    </div>
  </header>
);

export const IxeeFooter = () => (
  <footer className="border-t border-neutral-200 mt-24">
    <div className="max-w-6xl mx-auto px-5 py-10 flex flex-col md:flex-row gap-6 md:items-center md:justify-between">
      <div>
        <Wordmark className="text-xl" markClassName="h-7 w-7" />
        <p className="text-sm text-neutral-500 mt-1">
          Immersive Mix of Everything Essential. IXEE runs on the Goollooper platform.
        </p>
      </div>
      <nav className="flex gap-6 text-sm text-neutral-600">
        <Link to="/privacy" className="hover:text-[#161A1D]">Privacy</Link>
        <Link to="/terms" className="hover:text-[#161A1D]">Terms</Link>
        <Link to="/delete-account" className="hover:text-[#161A1D]">Delete account</Link>
      </nav>
    </div>
    <div className="max-w-6xl mx-auto px-5 pb-8 text-xs text-neutral-400">
      © 2026 IXEE. Support lives inside the app — just ask IXEE.
    </div>
  </footer>
);

const SectionTitle = ({ eyebrow, title, children }) => (
  <div className="max-w-2xl">
    <p className="text-xs font-semibold tracking-[0.2em] text-neutral-500 uppercase">{eyebrow}</p>
    <h2
      className="text-3xl md:text-4xl font-bold text-[#161A1D] mt-3"
      style={{ fontFamily: "'Space Grotesk', sans-serif" }}
    >
      {title}
    </h2>
    {children && <p className="text-neutral-600 mt-4 leading-relaxed">{children}</p>}
  </div>
);

const IxeeHome = () => (
  <div className="min-h-screen bg-white" style={{ fontFamily: "'Inter', sans-serif" }}>
    <IxeeNav />
    <main>
      {/* Hero */}
      <section className="max-w-6xl mx-auto px-5 pt-20 pb-16 text-center">
        <p className="text-xs font-semibold tracking-[0.25em] text-neutral-500 uppercase">
          Immersive Mix of Everything Essential
        </p>
        <h1
          className="text-5xl md:text-6xl font-bold text-[#161A1D] mt-5 leading-tight"
          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
        >
          IXEE — just say the word.
        </h1>
        <p className="text-lg text-neutral-600 max-w-2xl mx-auto mt-6 leading-relaxed">
          IXEE is a voice-and-chat assistant for real life — home services, local pros,
          goods, jobs and projects — matched, scheduled and followed up for you,
          from a single screen.
        </p>
        <div className="flex items-center justify-center gap-4 mt-9">
          <a
            href={APP_URL}
            className="bg-[#161A1D] text-white font-medium px-6 py-3 rounded-full hover:bg-black"
          >
            Open the web app
          </a>
          <a
            href="#how"
            className="border border-neutral-300 text-[#161A1D] font-medium px-6 py-3 rounded-full hover:border-neutral-500"
          >
            How it works
          </a>
        </div>
        <p className="text-sm text-neutral-400 mt-5">Text and voice-to-text are free.</p>
      </section>

      {/* How it works */}
      <section id="how" className="max-w-6xl mx-auto px-5 py-16">
        <SectionTitle eyebrow="How it works" title="One conversation, start to finish" />
        <div className="grid md:grid-cols-3 gap-6 mt-10">
          {[
            {
              n: "1",
              t: "Say it",
              d: "Tell IXEE what you need, by text or voice. Rose and Roc listen, ask the right questions, and understand the job.",
            },
            {
              n: "2",
              t: "Get matched",
              d: "IXEE finds the best-fit providers, listings and prices near you — Identity checks and quality-rating for every provider.",
            },
            {
              n: "3",
              t: "Consider it done",
              d: "Quotes, scheduling, reminders and follow-ups happen behind the scenes. You approve what matters; IXEE handles the rest.",
            },
          ].map((s) => (
            <div key={s.n} className="border border-neutral-200 rounded-2xl p-6">
              <div
                className="w-9 h-9 rounded-full bg-[#161A1D] text-white flex items-center justify-center font-bold"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                {s.n}
              </div>
              <h3 className="font-semibold text-lg text-[#161A1D] mt-4">{s.t}</h3>
              <p className="text-neutral-600 mt-2 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Providers */}
      <section id="providers" className="max-w-6xl mx-auto px-5 py-16">
        <SectionTitle eyebrow="For providers" title="Built for every kind of provider">
          Whether you're a solo pro, a micro-business with a small crew, a storefront,
          a recruiter or a merchant — IXEE brings you matched customers, not cold leads.
        </SectionTitle>
        <div className="flex flex-wrap gap-2 mt-8">
          {[
            "General users",
            "Business service providers",
            "Micro-businesses (2–9 workers)",
            "Business locations",
            "Job recruiters",
            "Merchandise providers & brokers",
            "Industry workers",
          ].map((chip) => (
            <span
              key={chip}
              className="border border-neutral-300 rounded-full px-4 py-1.5 text-sm text-neutral-700"
            >
              {chip}
            </span>
          ))}
        </div>
        <p className="text-neutral-600 mt-8 max-w-2xl leading-relaxed">
          Trust is built in: verified profiles, quality indicators and classifications
          mean customers can hire with confidence — and good work gets seen.
        </p>
      </section>

      {/* AdEd */}
      <section id="aded" className="max-w-6xl mx-auto px-5 py-16">
        <SectionTitle eyebrow="AdEd" title="Advertising that teaches">
          Short reels that mix offers with know-how. Providers share tips and showcase
          their work; customers learn something useful with every scroll. Boost-Plus
          puts your best in front of the right towns.
        </SectionTitle>
      </section>

      {/* Data Room */}
      <section id="dataroom" className="max-w-6xl mx-auto px-5 py-16">
        <SectionTitle eyebrow="Data Room" title="Everything, organized">
          Projects, sales pitches, niche pitches, files and quotes live in your Data
          Room — with REQ-Invoice documents your assistant creates and keeps up to date.
        </SectionTitle>
      </section>

      {/* App CTA */}
      <section className="max-w-6xl mx-auto px-5 py-16 text-center">
        <h2
          className="text-3xl md:text-4xl font-bold text-[#161A1D]"
          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
        >
          One screen is all you need.
        </h2>
        <p className="text-neutral-600 mt-4 max-w-xl mx-auto leading-relaxed">
          Everything in IXEE lives behind a single home screen and a conversation.
          No menus to learn, no apps to juggle.
        </p>
        <a
          href={APP_URL}
          className="inline-block bg-[#161A1D] text-white font-medium px-6 py-3 rounded-full hover:bg-black mt-8"
        >
          Launch the web app
        </a>
      </section>
    </main>
    <IxeeFooter />
  </div>
);

export default IxeeHome;
